import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";

import { CheckboxGroupField } from "~/components/form/checkbox-group-field";
import { ConditionalField } from "~/components/ui/conditional-field";
import { DiscoveryStep } from "~/components/ui/discovery-step";
import { FormField } from "~/components/ui/form-field";
import { Textarea } from "~/components/ui/textarea";
import { useQuickMatchContext } from "~/lib/discovery/quick-match-context";
import {
  type IdentifySpecificGoalsBaseSchema,
  IdentifySpecificGoalsSchema,
  goalsField,
  otherGoalField,
} from "~/lib/schemas/coach-discovery";

type GoalsForm = z.infer<typeof IdentifySpecificGoalsBaseSchema>;

export const Route = createFileRoute("/_public/discovery/match/")({
  component: QuickMatchGoals,
});

function QuickMatchGoals() {
  const navigate = useNavigate();
  const { state, update } = useQuickMatchContext();

  const { handleSubmit, control, watch, setFocus, formState } =
    useForm<GoalsForm>({
      resolver: zodResolver(IdentifySpecificGoalsSchema),
      defaultValues: {
        goals: state.goals ?? [],
        otherGoal: state.otherGoal ?? "",
      },
      mode: "onTouched",
      reValidateMode: "onChange",
    });

  const { errors, isSubmitting, isValid } = formState;
  const watchedGoals = watch("goals") ?? [];
  const tooManyGoals = watchedGoals.length > 2;

  const onSubmit = (data: GoalsForm) => {
    update({ goals: data.goals, otherGoal: data.otherGoal });
    navigate({ to: "/discovery/match/context" });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
      <DiscoveryStep.Title title="What are you looking to achieve?" />
      <DiscoveryStep.SubTitle title="Select up to 2 goals that matter most to you." />

      <DiscoveryStep.Card className="space-y-6">
        <FormField error={errors.goals?.message}>
          <CheckboxGroupField
            name="goals"
            control={control}
            options={goalsField.options ?? []}
            onTrigger={{ Other: () => setFocus("otherGoal") }}
          />
          {tooManyGoals && (
            <p className="mt-2 text-sm text-red-600">
              You cannot select more than 2 goals. Please uncheck one to
              continue.
            </p>
          )}
        </FormField>

        <ConditionalField show={watchedGoals.includes("Other")} indent>
          <FormField error={errors.otherGoal?.message}>
            <Controller
              name="otherGoal"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Textarea
                  value={field.value}
                  onValueChange={field.onChange}
                  maxLength={otherGoalField.maxLength}
                  placeholder={otherGoalField.placeholder}
                />
              )}
            />
          </FormField>
        </ConditionalField>
      </DiscoveryStep.Card>

      <DiscoveryStep.Control
        type="submit"
        loading={isSubmitting}
        disabled={!isValid}
      />
    </form>
  );
}
